import React, { useState, useRef } from 'react';
import { BannerElement } from '../types';
import { Image } from 'lucide-react';

interface CanvasElementProps {
  element: BannerElement;
  isSelected: boolean;
  processedContent?: string;
  processedImageUrl?: string;
  onSelect: () => void;
  onUpdate: (updates: Partial<BannerElement>) => void;
}

export const CanvasElement: React.FC<CanvasElementProps> = ({
  element,
  isSelected,
  processedContent,
  processedImageUrl,
  onSelect,
  onUpdate
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState<string>('');
  const dragRef = useRef<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  const resizeRef = useRef<{ startX: number; startY: number; initialWidth: number; initialHeight: number } | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onSelect();
    
    setIsDragging(true);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialX: element.x,
      initialY: element.y
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!dragRef.current) return;
      
      const deltaX = e.clientX - dragRef.current.startX;
      const deltaY = e.clientY - dragRef.current.startY;
      
      // Calculate new position
      const newX = dragRef.current.initialX + deltaX;
      const newY = dragRef.current.initialY + deltaY;
      
      // Allow positioning with some tolerance for elements to extend slightly beyond canvas
      // This gives more flexibility while still maintaining general containment
      onUpdate({
        x: newX,
        y: newY
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleResizeMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    setResizeHandle(handle);
    resizeRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      initialWidth: element.width,
      initialHeight: element.height
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeRef.current) return;
      
      const deltaX = e.clientX - resizeRef.current.startX;
      const deltaY = e.clientY - resizeRef.current.startY;
      
      let newWidth = resizeRef.current.initialWidth;
      let newHeight = resizeRef.current.initialHeight;
      let newX = element.x;
      let newY = element.y;

      if (handle.includes('right')) {
        newWidth = Math.max(10, resizeRef.current.initialWidth + deltaX);
      }
      if (handle.includes('left')) {
        newWidth = Math.max(10, resizeRef.current.initialWidth - deltaX);
        newX = element.x + deltaX;
      }
      if (handle.includes('bottom')) {
        newHeight = Math.max(10, resizeRef.current.initialHeight + deltaY);
      }
      if (handle.includes('top')) {
        newHeight = Math.max(10, resizeRef.current.initialHeight - deltaY);
        newY = element.y + deltaY;
      }

      // Maintain aspect ratio for images when using corner handles
      if (element.type === 'image' && element.originalAspectRatio && handle.includes('corner')) {
        const aspectRatio = element.originalAspectRatio;
        if (handle.includes('right') || handle.includes('left')) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }

      onUpdate({
        width: newWidth,
        height: newHeight,
        x: newX,
        y: newY
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeHandle('');
      resizeRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const elementStyle: React.CSSProperties = {
    position: 'absolute',
    left: element.x,
    top: element.y,
    width: element.width,
    height: element.height,
    zIndex: element.zIndex,
    cursor: isDragging ? 'grabbing' : 'grab',
    fontSize: element.styles.fontSize,
    fontWeight: element.styles.fontWeight,
    fontFamily: element.styles.fontFamily,
    textAlign: element.styles.textAlign,
    color: element.styles.color,
    backgroundColor: element.styles.backgroundColor,
    borderRadius: element.styles.borderRadius,
    padding: element.styles.padding,
    margin: element.styles.margin,
    boxShadow: element.styles.boxShadow,
    border: element.styles.border,
    opacity: element.styles.opacity,
    transform: element.styles.transform,
    outline: isSelected ? '2px solid #3B82F6' : 'none',
    outlineOffset: '2px'
  };

  const renderResizeHandles = () => {
    if (!isSelected) return null;

    const handles = [
      { position: 'top-left corner', cursor: 'nw-resize', style: { top: -8, left: -8 } },
      { position: 'top-right corner', cursor: 'ne-resize', style: { top: -8, right: -8 } },
      { position: 'bottom-left corner', cursor: 'sw-resize', style: { bottom: -8, left: -8 } },
      { position: 'bottom-right corner', cursor: 'se-resize', style: { bottom: -8, right: -8 } },
      { position: 'top', cursor: 'n-resize', style: { top: -8, left: '50%', transform: 'translateX(-50%)' } },
      { position: 'bottom', cursor: 's-resize', style: { bottom: -8, left: '50%', transform: 'translateX(-50%)' } },
      { position: 'left', cursor: 'w-resize', style: { left: -8, top: '50%', transform: 'translateY(-50%)' } },
      { position: 'right', cursor: 'e-resize', style: { right: -8, top: '50%', transform: 'translateY(-50%)' } }
    ];

    return (
      <>
        {handles.map(handle => (
          <div
            key={handle.position}
            className="absolute w-4 h-4 bg-blue-500 border-2 border-white rounded-sm shadow-md hover:bg-blue-600 transition-colors"
            style={{
              ...handle.style,
              cursor: handle.cursor,
              zIndex: 1000
            }}
            onMouseDown={(e) => handleResizeMouseDown(e, handle.position)}
          />
        ))}
      </>
    );
  };

  const renderImagePlaceholder = () => {
    return (
      <div className="w-full h-full bg-gray-100 border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500">
        <Image size={24} className="mb-2" />
        <span className="text-xs font-medium">Image Placeholder</span>
        <span className="text-xs">Select field in properties</span>
      </div>
    );
  };

  const renderElement = () => {
    switch (element.type) {
      case 'text':
        return (
          <div
            style={elementStyle}
            onMouseDown={handleMouseDown}
            className="flex items-center justify-center select-none relative"
          >
            {processedContent || element.content || 'Text'}
            {renderResizeHandles()}
          </div>
        );

      case 'image':
        const hasImageUrl = processedImageUrl || element.imageUrl;
        return (
          <div
            style={elementStyle}
            onMouseDown={handleMouseDown}
            className="overflow-hidden relative"
          >
            {hasImageUrl ? (
              <img
                src={hasImageUrl}
                alt="Banner Image"
                className="w-full h-full object-cover select-none"
                draggable={false}
                onLoad={(e) => {
                  // Calculate and store aspect ratio when image loads
                  const img = e.target as HTMLImageElement;
                  const aspectRatio = img.naturalWidth / img.naturalHeight;
                  onUpdate({ originalAspectRatio: aspectRatio });
                }}
              />
            ) : (
              renderImagePlaceholder()
            )}
            {renderResizeHandles()}
          </div>
        );

      case 'shape':
        return (
          <div
            style={elementStyle}
            onMouseDown={handleMouseDown}
            className="select-none relative"
          >
            {renderResizeHandles()}
          </div>
        );

      case 'button':
        return (
          <button
            style={{
              ...elementStyle,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: 'none',
              cursor: isDragging ? 'grabbing' : 'pointer'
            }}
            onMouseDown={handleMouseDown}
            className="select-none transition-all hover:opacity-80 relative"
          >
            {processedContent || element.content || 'Button'}
            {renderResizeHandles()}
          </button>
        );

      case 'divider':
        return (
          <div
            style={{
              ...elementStyle,
              height: element.height || 2
            }}
            onMouseDown={handleMouseDown}
            className="select-none relative"
          >
            {renderResizeHandles()}
          </div>
        );

      default:
        return null;
    }
  };

  return renderElement();
};